import { TrainingCourse, Quiz } from '@/models/training.model';
import { TrainingType } from '@/models/training.model';

// Helper to create quiz from simple format
function createQuiz(questions: { question: string; options: string[]; correct: number }[]): Quiz {
  return {
    id: `quiz-${Date.now()}`,
    questions: questions.map((q, i) => ({
      id: `q-${i}`,
      question: q.question,
      type: 'multiple_choice' as const,
      options: q.options,
      correctAnswer: q.options[q.correct],
      points: 1,
    })),
  };
}

// Content is stored directly in the training course content field

export const HIPAA_TRAINING_MODULES: Partial<TrainingCourse>[] = [
  {
    title: 'HIPAA Compliance for Executives',
    description: 'Understand organizational liability under HIPAA, executive responsibilities, and compliance oversight',
    type: TrainingType.compliance,
    category: 'HIPAA',
    duration: 45,
    passingScore: 70,
    isRequired: true,
    isActive: true,
    tags: ['hipaa', 'compliance', 'executive', 'required'],
    quiz: createQuiz([
      {
        question: 'What is the maximum annual penalty per HIPAA violation category?',
        options: ['$100,000', '$500,000', '$1.5 million', '$10 million'],
        correct: 2,
      },
      {
        question: 'How long must HIPAA documentation be retained?',
        options: ['2 years', '4 years', '6 years', '10 years'],
        correct: 2,
      },
      {
        question: 'Which role must be designated under HIPAA?',
        options: ['CEO only', 'Privacy Officer and Security Officer', 'HR Director', 'Legal Counsel'],
        correct: 1,
      },
    ]),
  },
  {
    title: 'HIPAA Compliance for Managers',
    description: 'Identify PHI in daily operations, apply the Minimum Necessary standard, and supervise team compliance',
    type: TrainingType.compliance,
    category: 'HIPAA',
    duration: 60,
    passingScore: 70,
    isRequired: true,
    isActive: true,
    tags: ['hipaa', 'compliance', 'management', 'required'],
    quiz: createQuiz([
      {
        question: 'What does the Minimum Necessary standard require?',
        options: ['Share all PHI to be thorough', 'Access only the PHI needed for the specific task', 'Keep PHI for minimum time', 'Use minimum security'],
        correct: 1,
      },
      {
        question: 'When should a potential HIPAA incident be reported?',
        options: ['Within 1 week', 'Within 24 hours', 'At the next team meeting', 'Only if confirmed as a breach'],
        correct: 1,
      },
      {
        question: 'Can you paste PHI into ChatGPT to help with a task?',
        options: ['Yes, if it\'s work-related', 'Yes, if you remove the name', 'No, AI tools are not approved for PHI', 'Only executives can approve this'],
        correct: 2,
      },
    ]),
  },
  {
    title: 'HIPAA Essentials for All Employees',
    description: 'Recognize PHI, follow security best practices, and know what to do if you suspect a violation',
    type: TrainingType.compliance,
    category: 'HIPAA',
    duration: 45,
    passingScore: 70,
    isRequired: true,
    isActive: true,
    tags: ['hipaa', 'compliance', 'staff', 'required'],
    quiz: createQuiz([
      {
        question: 'Which of the following is PHI?',
        options: ['A blank reimbursement form', 'An employee\'s medical receipt with their name', 'General healthcare pricing information', 'Company policy documents'],
        correct: 1,
      },
      {
        question: 'What should you do if you accidentally see PHI you shouldn\'t have?',
        options: ['Delete it and forget about it', 'Report it to your manager immediately', 'Tell a coworker about it', 'Screenshot it for evidence'],
        correct: 1,
      },
      {
        question: 'When should you lock your computer screen?',
        options: ['Only at the end of the day', 'Every time you step away', 'Only if you have PHI open', 'Every hour'],
        correct: 1,
      },
    ]),
  },
];

export const COMPANY_TRAINING_MODULES: Partial<TrainingCourse>[] = [
  {
    title: 'FairCare Plus Business Model for Executives',
    description: 'Articulate the FairCare Plus value proposition, understand competitive positioning, and communicate compliance',
    type: TrainingType.onboarding,
    category: 'Company Orientation',
    duration: 40,
    passingScore: 70,
    isRequired: true,
    isActive: true,
    tags: ['company', 'orientation', 'executive', 'business-model'],
    quiz: createQuiz([
      {
        question: 'What type of arrangement is FairCare Plus structured as?',
        options: ['Traditional insurance', 'ERISA-compliant HRA', 'FSA', 'HSA'],
        correct: 1,
      },
      {
        question: 'What is the typical savings vs. insurance rates?',
        options: ['5-10%', '10-20%', '30-70%', '80-90%'],
        correct: 2,
      },
      {
        question: 'Can FairCare Plus work alongside traditional insurance?',
        options: ['No, it replaces insurance entirely', 'Yes, many employers use both', 'Only for large employers', 'Only for healthcare providers'],
        correct: 1,
      },
    ]),
  },
  {
    title: 'FairCare Plus Operations for Managers',
    description: 'Understand the end-to-end reimbursement workflow, support employers and employees, and handle common questions',
    type: TrainingType.onboarding,
    category: 'Company Orientation',
    duration: 50,
    passingScore: 70,
    isRequired: true,
    isActive: true,
    tags: ['company', 'orientation', 'management', 'operations'],
    quiz: createQuiz([
      {
        question: 'How do employees join FairCare Plus?',
        options: ['They sign up individually online', 'Their employer enrolls them', 'Through a healthcare provider', 'Via insurance broker'],
        correct: 1,
      },
      {
        question: 'What is NOT a benefit for providers?',
        options: ['Guaranteed upfront payment', 'No claims processing', 'Higher negotiated rates', 'No denials or reprocessing'],
        correct: 2,
      },
      {
        question: 'Is FairCare Plus insurance?',
        options: ['Yes, it\'s a new type of insurance', 'No, it\'s an ERISA-compliant HRA', 'It depends on the state', 'Only for large employers'],
        correct: 1,
      },
    ]),
  },
  {
    title: 'Welcome to FairCare Plus',
    description: 'Understand what FairCare Plus does, know our mission and values, and recognize how your role contributes',
    type: TrainingType.onboarding,
    category: 'Company Orientation',
    duration: 30,
    passingScore: 70,
    isRequired: true,
    isActive: true,
    tags: ['company', 'orientation', 'staff', 'onboarding'],
    quiz: createQuiz([
      {
        question: 'What does FairCare Plus do?',
        options: ['Sell insurance policies', 'Help employers provide transparent, cash-pay healthcare benefits', 'Run hospitals', 'Manufacture medicine'],
        correct: 1,
      },
      {
        question: 'How do employees get reimbursed?',
        options: ['They file a claim with insurance', 'They upload a receipt and get reimbursed in days', 'They wait 6-8 weeks for a check', 'They can\'t get reimbursed'],
        correct: 1,
      },
      {
        question: 'Which is TRUE about FairCare Plus?',
        options: ['Employees must use specific network doctors', 'There are no network restrictions', 'It only works for large companies', 'It costs more than insurance'],
        correct: 1,
      },
    ]),
  },
];

// Employee level types
export type EmployeeLevel = 'executive' | 'management' | 'staff';

// Get training modules for a specific level
export function getTrainingModulesForLevel(level: EmployeeLevel): {
  hipaa: Partial<TrainingCourse>;
  company: Partial<TrainingCourse>;
} {
  const hipaaIndex = level === 'executive' ? 0 : level === 'management' ? 1 : 2;
  const companyIndex = level === 'executive' ? 0 : level === 'management' ? 1 : 2;

  return {
    hipaa: HIPAA_TRAINING_MODULES[hipaaIndex] || HIPAA_TRAINING_MODULES[2],
    company: COMPANY_TRAINING_MODULES[companyIndex] || COMPANY_TRAINING_MODULES[2],
  };
}
